import React, { useMemo } from 'react';

import { Divider, FormControl, FormLabel, Grid, type GridProps, MenuItem, Select } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import ControlledInput from '../../../../../components/forms/ControlledInput';
import { ClassStatus } from '../../../../../context/types';
import { OrganizationType, useSchoolInfo } from '../../../../../hooks/useSchoolInfo';
import { ClassContainer } from '../../../../ClassModal/ClassContainer';
import ClassFormErrors from '../../../../ClassModal/ClassFormErrors';
import Advanced from '../../../../ClassModal/Sections/Advanced';
import Instructor from '../../../../ClassModal/Sections/Advanced/Instructor';
import Dates from '../../../../ClassModal/Sections/Dates';
import ClassDetailEditFormActions from '../ClassDetailEditFormActions';

export const RequiredIcon = () => (
    <Typography component="span" color="error">
        *
    </Typography>
);

const AdvancedContainer = styled('div')`
    margin-bottom: 2rem;
`;

const ErrorContainer = styled('div')`
    margin-bottom: 2rem;
`;

const StyledDivider = styled(Divider)`
    margin: 2rem 0;
`;

type ClassDetailEditFormProps = {
    onSubmit: () => void;
};

const FormControlGrid = (props: GridProps) => (
    <Grid container sx={{ mt: 2 }} {...props}>
        {props.children}
    </Grid>
);

export const ClassDetailEditForm = (props: ClassDetailEditFormProps) => {
    const { onSubmit } = props;
    const { t } = useTranslation(['create_edit_class', 'admin_settings']);
    const { classRecord, control, isTeams } = useContainer(ClassContainer);

    const mainInstructor = useMemo(() => `${classRecord?.first_name} ${classRecord?.last_name}`, [classRecord]);

    const { data: school } = useSchoolInfo();

    const renderInstructorInfo = () => {
        if ((school && school.type === OrganizationType.Teams) || classRecord?.status === ClassStatus.Draft) {
            return null;
        }

        return (
            <>
                <Typography fontWeight="bold">{t('admin_settings:classes.lead-instructor')}</Typography>
                <Typography>{t('admin_settings:classes.lead-instructor-description')}</Typography>

                <Box sx={{ mt: 3 }}>
                    <FormControl size="small">
                        <FormLabel>{t('admin_settings:classes.lead-instructor')}</FormLabel>
                        <Select value={mainInstructor} disabled>
                            <MenuItem value={mainInstructor}>{mainInstructor}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <StyledDivider />
            </>
        );
    };

    return (
        <form onSubmit={onSubmit}>
            {renderInstructorInfo()}

            <FormControlGrid>
                <Grid item xs={12} md={6} lg={6}>
                    <FormControl fullWidth>
                        <ControlledInput
                            type="text"
                            name="className"
                            control={control}
                            label={
                                <>
                                    {t('create_edit_class:details.class-name')} <RequiredIcon />
                                </>
                            }
                        />
                    </FormControl>
                </Grid>
            </FormControlGrid>

            <FormControlGrid>
                <Grid item xs={12} md={6} lg={6}>
                    <FormControl fullWidth>
                        <ControlledInput
                            type="text"
                            name="classId"
                            control={control}
                            label={t('create_edit_class:details.class-id')}
                        />
                    </FormControl>
                </Grid>
            </FormControlGrid>

            <FormControlGrid>
                <Grid item xs={12} md={6} lg={6}>
                    <FormControl fullWidth>
                        <ControlledInput
                            type="text"
                            multiline
                            name="description"
                            control={control}
                            label={t('create_edit_class:details.description')}
                        />
                    </FormControl>
                </Grid>
            </FormControlGrid>

            {!isTeams && (
                <FormControlGrid>
                    <Grid item xs={12} md={6} lg={6}>
                        <FormControl fullWidth>
                            <ControlledInput
                                type="text"
                                inputProps={{ maxLength: 10 }}
                                name="classPasscode"
                                control={control}
                                label={
                                    <>
                                        {t('create_edit_class:details.class-passcode')} <RequiredIcon />
                                    </>
                                }
                            />
                        </FormControl>
                    </Grid>
                </FormControlGrid>
            )}

            {classRecord?.status === ClassStatus.Draft && (
                <FormControlGrid>
                    <Grid item xs={12} md={6} lg={6}>
                        <Instructor />
                    </Grid>
                </FormControlGrid>
            )}

            <StyledDivider />

            <FormControlGrid>
                <Grid item xs={12} md={12} lg={12}>
                    <Dates />
                </Grid>
            </FormControlGrid>

            <StyledDivider />

            <AdvancedContainer>
                <Advanced parentPage="admin" />
            </AdvancedContainer>

            <ErrorContainer>
                <ClassFormErrors />
            </ErrorContainer>

            <ClassDetailEditFormActions />
        </form>
    );
};
